import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { IdCard, Loader2, CheckCircle2 } from 'lucide-react';
import { validateDob, validateAhv, validateIban, maskDob, maskAhv, maskIban } from '@/lib/swissValidators';
import { ChildrenEditor, childrenValid } from '@/components/staff/ChildrenEditor';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELDS = [
  ['ahv_number', 'AHV-Nummer', '756.XXXX.XXXX.XX'],
  ['date_of_birth', 'Geburtsdatum', 'TT.MM.JJJJ'],
  ['nationality', 'Nationalität', 'z. B. CH'],
  ['civil_status', 'Zivilstand', 'ledig / verheiratet / …'],
  ['address_street', 'Strasse & Nr.', 'Bahnhofstrasse 1'],
  ['address_zip', 'PLZ', '8620'],
  ['address_city', 'Ort', 'Wetzikon'],
  ['iban', 'IBAN', 'CH..'],
  ['bank_name', 'Bank', 'z. B. UBS'],
  ['mobile', 'Natel', '+41 79 …'],
];

const numInvalid = (v) => v !== '' && v != null && !(parseFloat(v) >= 0);
const numOrNull = (v) => (v === '' || v == null ? null : parseFloat(v));

export const EmployeeMasterDataButton = ({ userId, name, asMenuItem = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [children, setChildren] = useState([]);
  const [wage, setWage] = useState({ wage_type: 'fixed', hourly_wage: '', monthly_salary: '' });
  const [work, setWork] = useState({ weekly_hours: '' });
  const [workRaw, setWorkRaw] = useState({});
  const [meta, setMeta] = useState({});

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const [md, pay, wk] = await Promise.all([
        axios.get(`${API}/employee/master-data/${userId}`, { headers: headers() }),
        axios.get(`${API}/timetracking/payroll/settings/${userId}`, { headers: headers() }).catch(() => ({ data: {} })),
        axios.get(`${API}/timetracking/trainers/${userId}/settings`, { headers: headers() }).catch(() => ({ data: {} })),
      ]);
      const d = md.data || {};
      const f = {};
      FIELDS.forEach(([k]) => { f[k] = d[k] || ''; });
      setForm(f);
      setChildren(Array.isArray(d.children) ? d.children : []);
      setMeta({ onboarding_status: d.onboarding_status, erp_synced_at: d.erp_synced_at });
      const p = pay.data || {};
      setWage({
        wage_type: p.wage_type === 'hourly' ? 'hourly' : 'fixed',
        hourly_wage: p.hourly_wage != null && p.hourly_wage !== 0 ? String(p.hourly_wage) : '',
        monthly_salary: p.monthly_salary != null && p.monthly_salary !== 0 ? String(p.monthly_salary) : '',
      });
      const w = wk.data || {};
      setWorkRaw(w);
      setWork({ weekly_hours: w.weekly_hours != null ? String(w.weekly_hours) : '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stammdaten konnten nicht geladen werden.');
    } finally { setLoading(false); }
  };

  const onOpenChange = (v) => { setOpen(v); if (v) load(); };

  const fieldError = (k, val) => {
    if (k === 'date_of_birth') return validateDob(val);
    if (k === 'ahv_number') return validateAhv(val);
    if (k === 'iban') return validateIban(val);
    return '';
  };
  const maskFor = (k, val) => {
    if (k === 'date_of_birth') return maskDob(val);
    if (k === 'ahv_number') return maskAhv(val);
    if (k === 'iban') return maskIban(val);
    return val;
  };
  const wageInvalid = numInvalid(wage.hourly_wage) || numInvalid(wage.monthly_salary) || numInvalid(work.weekly_hours);
  const anyInvalid = FIELDS.some(([k]) => !!fieldError(k, form[k])) || !childrenValid(children) || wageInvalid;

  const save = async () => {
    const firstErr = validateAhv(form.ahv_number) || validateIban(form.iban) || validateDob(form.date_of_birth);
    if (firstErr) { toast.error(firstErr); return; }
    if (!childrenValid(children)) { toast.error('Bitte bei jedem Kind Vorname und gültiges Geburtsdatum (TT.MM.JJJJ) angeben.'); return; }
    if (wageInvalid) { toast.error('Lohn/Sollstunden: Bitte gültige Zahlen eingeben.'); return; }
    setSaving(true);
    try {
      // 1) Lohnart/Fixlohn/Stundenlohn
      await axios.put(`${API}/timetracking/payroll/settings/${userId}`, {
        wage_type: wage.wage_type,
        hourly_wage: numOrNull(wage.hourly_wage),
        monthly_salary: numOrNull(wage.monthly_salary),
      }, { headers: headers() });
      // 2) Sollstunden (volles Work-Settings-Objekt mergen, um nichts zu überschreiben)
      if (work.weekly_hours !== '') {
        await axios.put(`${API}/timetracking/trainers/${userId}/settings`,
          { ...workRaw, weekly_hours: parseFloat(work.weekly_hours) }, { headers: headers() });
      }
      // 3) Stammdaten + Kinder → triggert ERP-Sync mit aktualisiertem Lohn
      const body = {};
      Object.entries(form).forEach(([k, v]) => { if (v && v.trim()) body[k] = v.trim(); });
      body.children = children.filter((c) => c.first_name && c.first_name.trim());
      const r = await axios.put(`${API}/employee/master-data/${userId}`, body, { headers: headers() });
      toast.success(r.data?.erp_synced ? 'Gespeichert & ans ERP übermittelt.' : 'Gespeichert.');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {asMenuItem ? (
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); onOpenChange(true); }}
          data-testid={`employee-masterdata-btn-${userId}`}
        >
          <IdCard size={15} className="mr-2" />
          Stammdaten
        </DropdownMenuItem>
      ) : (
        <Button variant="ghost" size="sm" onClick={() => onOpenChange(true)} data-testid={`employee-masterdata-btn-${userId}`}>
          <IdCard size={15} className="mr-1.5" />
          Stammdaten
        </Button>
      )}
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Stammdaten — {name || 'Mitarbeiter'}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            {(meta.onboarding_status || meta.erp_synced_at) && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {meta.onboarding_status && (
                  <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid="employee-onboarding-status">
                    Onboarding: {meta.onboarding_status === 'completed' ? 'abgeschlossen' : 'offen'}
                  </span>
                )}
                {meta.erp_synced_at && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> ERP synchronisiert
                  </span>
                )}
              </div>
            )}
            <Tabs defaultValue="stammdaten">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="stammdaten" data-testid="tab-stammdaten">Stammdaten</TabsTrigger>
                <TabsTrigger value="lohn" data-testid="tab-lohn">Lohn & Pensum</TabsTrigger>
              </TabsList>

              <TabsContent value="stammdaten" className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  {FIELDS.map(([k, label, ph]) => {
                    const err = fieldError(k, form[k]);
                    return (
                      <div key={k} className="space-y-1.5">
                        <Label className="text-xs">{label}</Label>
                        <Input
                          value={form[k] || ''}
                          placeholder={ph}
                          onChange={(e) => setForm({ ...form, [k]: maskFor(k, e.target.value) })}
                          className={err ? 'border-red-500 focus-visible:ring-red-500' : ''}
                          data-testid={`employee-field-${k}`}
                        />
                        {err ? <p className="text-[11px] text-red-500" data-testid={`employee-field-error-${k}`}>{err}</p> : null}
                      </div>
                    );
                  })}
                </div>
                <div className="pt-2 border-t">
                  <ChildrenEditor value={children} onChange={setChildren} />
                </div>
              </TabsContent>

              <TabsContent value="lohn" className="space-y-4 mt-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Lohnart</Label>
                  <select
                    value={wage.wage_type}
                    onChange={(e) => setWage({ ...wage, wage_type: e.target.value })}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="wage-type-select"
                  >
                    <option value="fixed">Fixlohn (Monatslohn)</option>
                    <option value="hourly">Stundenlohn</option>
                  </select>
                </div>
                {wage.wage_type === 'hourly' ? (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Stundenlohn (CHF / Stunde)</Label>
                    <Input
                      type="number" step="0.05" min="0" placeholder="z. B. 25.00"
                      value={wage.hourly_wage}
                      onChange={(e) => setWage({ ...wage, hourly_wage: e.target.value })}
                      className={numInvalid(wage.hourly_wage) ? 'border-red-500' : ''}
                      data-testid="wage-hourly-input"
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Fixlohn (CHF / Monat)</Label>
                    <Input
                      type="number" step="0.05" min="0" placeholder="z. B. 2250.00"
                      value={wage.monthly_salary}
                      onChange={(e) => setWage({ ...wage, monthly_salary: e.target.value })}
                      className={numInvalid(wage.monthly_salary) ? 'border-red-500' : ''}
                      data-testid="wage-monthly-input"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">Sollstunden (pro Woche)</Label>
                  <Input
                    type="number" step="0.5" min="0" placeholder="z. B. 42"
                    value={work.weekly_hours}
                    onChange={(e) => setWork({ weekly_hours: e.target.value })}
                    className={numInvalid(work.weekly_hours) ? 'border-red-500' : ''}
                    data-testid="work-weekly-hours-input"
                  />
                  <p className="text-[11px] text-muted-foreground">Vertragliche Soll-Arbeitszeit pro Woche (für Zeiterfassung &amp; Pensum).</p>
                </div>
              </TabsContent>
            </Tabs>

            <p className="text-xs text-muted-foreground">
              Änderungen werden gespeichert und direkt ans Lohnsystem (ERP) übermittelt. Abzüge/Zulagen berechnet das ERP.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={saving || loading || anyInvalid} data-testid="employee-masterdata-save">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
